<template>
  <div class="balavedi-main">
    <div class="balavedi-header">
      <input
        v-model="search"
        type="text"
        class="form-control balavedi-search"
        placeholder="Search madrasas..."
      />
      <button
        class="btn-pdf"
        :disabled="pdfLoading"
        @click="downloadPdf"
        title="Export PDF"
      >
        <span v-if="pdfLoading" class="pdf-spinner"></span>
        <ion-icon v-else name="document-text-outline"></ion-icon>
      </button>
    </div>
    
    <!-- Loading state -->
    <div v-if="loading" class="text-center mt-4">
      <div class="spinner-border text-secondary" role="status"></div>
    </div>
    
    <div v-else-if="filteredMadrasas.length" class="balavedi-list">
      <div
        v-for="(madrasa, index) in filteredMadrasas"
        :key="madrasa.id"
        class="balavedi-card"
        @click="goToMadrasa(madrasa)"
        tabindex="0"
        @keyup.enter="goToMadrasa(madrasa)"
      >
        <div class="madrasa-info">
          <span class="index-number">{{ index + 1 }}.</span>
          <span class="balavedi-name">{{ madrasa.name }}</span>
        </div>
        <button class="btn-arrow" @click.stop="goToMadrasa(madrasa)">
          <ion-icon name="chevron-forward-outline"></ion-icon>
        </button>
      </div>
    </div>
    <div v-else class="no-madrasas">
      <i class="fa fa-university fa-2x mb-2 text-muted"></i>
      <div>{{ loading ? 'Loading...' : 'No madrasas found.' }}</div>
    </div>
  </div>
</template>

<script>
import UserService from "../../services/user.service";

export default {
  name: 'BalavediList',
  data() {
    return {
      search: '',
      madrasas: [],
      loading: false,
      pdfLoading: false,
      error: null,
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.auth.user;
    },
    filteredMadrasas() {
      if (!this.search) return this.madrasas;
      return this.madrasas.filter(m =>
        m.name.toLowerCase().includes(this.search.toLowerCase())
      );
    },
  },
  watch: {
    '$route.params.id': {
      handler(newId) {
        if (newId) {
          this.fetchMadrasas();
        }
      },
      immediate: false
    },
    '$route.params.sub_level'() {
      this.fetchMadrasas();
    },
  },
  mounted() {
    this.fetchMadrasas();
  },
  methods: {
    async fetchMadrasas() {
      try {
        this.loading = true;
        this.error = null;

        const subLevel = parseInt(this.$route.params.sub_level, 10);
        const routeId = this.$route.params.id;
        const data = {};

        if (subLevel === 3) {
          data.zone_id = routeId || this.currentUser?.zone_id;
          if (this.currentUser?.district_id) {
            data.district_id = this.currentUser.district_id;
          }
        } else {
          data.district_id = routeId || this.currentUser?.district_id;
        }

        const response = await UserService.authGetRequest('madrassas', data);
        
        if (response.data.status === 'success') {
          // Fix: Access the correct path for madrasa data
          this.madrasas = response.data.data.list || [];
        } else {
          this.error = response.data.message || 'Failed to fetch madrasas';
          this.$swal('Error', this.error, 'error');
        }
      } catch (error) {
        this.error = 'Error fetching madrasas';
        console.error('Error fetching madrasas:', error);
        this.$swal('Error', 'Failed to load madrasas', 'error');
      } finally {
        this.loading = false;
      }
    },
    
    async downloadPdf() {
      this.pdfLoading = true;
      try {
        const subLevel = parseInt(this.$route.params.sub_level, 10);
        const routeId = this.$route.params.id;
        const payload = {};

        if (subLevel === 3) {
          payload.zone_id = routeId || this.currentUser?.zone_id;
          if (this.currentUser?.district_id) {
            payload.district_id = this.currentUser.district_id;
          }
        } else {
          payload.district_id = routeId || this.currentUser?.district_id;
        }

        const response = await UserService.authPostRequest(
          'balavedi/zone-pdf',
          payload,
          { responseType: 'blob' }
        );

        const blob = response.data;
        const contentType = (response.headers && response.headers['content-type']) || '';
        if (contentType.includes('application/json')) {
          const text = await new Promise((res) => {
            const r = new FileReader();
            r.onload = () => res(r.result);
            r.readAsText(blob);
          });
          const json = JSON.parse(text);
          this.$swal('Error', json.message || 'Failed to generate PDF', 'error');
          return;
        }

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Balavedi-Zone-Report.pdf';
        a.click();
        URL.revokeObjectURL(url);
      } catch (err) {
        this.$swal('Error', 'Failed to download PDF', 'error');
      } finally {
        this.pdfLoading = false;
      }
    },

    goToMadrasa(madrasa) {
      this.$router.push({ 
        name: 'BalavediMadrasaMembers',
        params: { 
          sub_level: this.$route.params.sub_level, 
          id: this.$route.params.id, 
          madrasa_id: madrasa.id 
        } 
      });
    },
    
    // Method to create or update member (for future use)
    async createOrUpdateMember(memberData) {
      try {
        const response = await UserService.authPostRequest('balavedi/members/create-or-update', memberData);
        
        if (response.data.status === 'success') {
          this.$swal('Success', response.data.message, 'success');
          return response.data;
        } else {
          this.$swal('Error', response.data.message, 'error');
          throw new Error(response.data.message);
        }
      } catch (error) {
        console.error('Error creating/updating member:', error);
        this.$swal('Error', 'Failed to save member', 'error');
        throw error;
      }
    },
    
    // Method to get members by madrasa (for future use)
    async getMembersByMadrasa(madrasaId) {
      try {
        const response = await UserService.authGetRequest(`balavedi/members/madrassa/${madrasaId}`);
        
        if (response.data.status === 'success') {
          return response.data.data || [];
        } else {
          throw new Error(response.data.message);
        }
      } catch (error) {
        console.error('Error fetching members:', error);
        this.$swal('Error', 'Failed to load members', 'error');
        throw error;
      }
    },
  },
};
</script>

<style scoped>
.balavedi-main {
  max-width: 420px;
  margin: 20px auto;
  padding: 0 18px;
}
.balavedi-header {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}
.balavedi-search {
  flex: 1;
}
.btn-pdf {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 8px;
  background: #1a418c;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.18s;
  font-size: 1.3rem;
}
.btn-pdf:hover:not(:disabled) {
  background: #153070;
}
.btn-pdf:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.pdf-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255,255,255,0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  display: inline-block;
}
@keyframes spin { to { transform: rotate(360deg); } }
.balavedi-madrasa-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.balavedi-card {
  background: #fff;
  border-radius: 10px;
  padding: 10px 15px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
  margin-bottom: 5px;
  cursor: pointer;
  transition: box-shadow 0.18s, transform 0.18s;
}
.balavedi-card:hover, .balavedi-card:focus {
  box-shadow: 0 4px 12px rgba(30, 64, 175, 0.10);
  transform: translateY(-2px) scale(1.01);
}
.balavedi-name {
  color: #1a418c;
  font-size: 1.08rem;
  font-weight: 600;
}
.btn-arrow {
    border: none;
    background: transparent;
    color: #6c757d;
    padding: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: transform 0.2s;
}

.btn-arrow ion-icon {
    font-size: 1.4rem;
}

.balavedi-card:hover .btn-arrow {
    transform: translateX(3px);
}
.no-madrasas {
  text-align: center;
  color: #888;
  font-size: 1.1rem;
  margin-top: 40px;
}
.madrasa-info {
  display: flex;
  align-items: center;
  gap: 12px;
}
.index-number {
  color: #6c757d;
  font-weight: 500;
  min-width: 24px;
}
</style>
